import { Link } from "react-router-dom";
import { Lock, ArrowRight, Eye } from "lucide-react";
import { FEATURES, previewPath } from "@/lib/planFeatures";
import { trackUpgrade, withSrc } from "@/lib/upgradeTrack";
import { useEntitlements } from "@/lib/useEntitlements";

const COPY = {
  invoicing: ["Owner invoicing", "Bill every show from your phone and keep 100% of it — owners cover the fees."],
  pay_links: ["Pay links", "Card and bank payments with the fee on the owner's side."],
  owner_portal: ["Owner Portal", "Owners see their horses, invoices and receipts without texting you."],
  books: ["Books", "Income, expenses and per-show P&L without QuickBooks."],
  expenses: ["Expenses", "Track show expenses and split shared costs across owners."],
  exports: ["Exports", "PDF and CSV exports for your accountant."],
  ask_kingly: ["Ask Kingly", "Ask anything about your barn and get an answer from your own data."],
  bookkeeper_seat: ["Bookkeeper seat", "Give your bookkeeper their own login."],
  tax_exports: ["Year-end tax exports", "1099s, mileage and receipts in one export."],
  hauling: ["Hauling & trip billing", "Bill hauling by the mile, split by horse."],
  vet_records: ["Vet records & health papers", "Coggins, vaccinations and health papers per horse — with expiry you can see before you enter."],
  home_barn: ["Home barn whiteboard", "The everyday board for every horse, daily routines, and billable checks that land on the monthly bill."],
  breeding: ["Breeding records", "Mares, breedings, embryos and foals — with billing hooks, mare reports and foaling reminders."],
  spanish_assistant: ["Spanish assistant app", "Your grooms work in Spanish — board, tasks, reminders and help — while you read it in English."],
};
const PREVIEW = Object.fromEntries(Object.keys(FEATURES).map((k) => [k, [FEATURES[k].interactive || previewPath(k), FEATURES[k].interactive ? "Peek at a demo" : "See a preview"]]));
export const PRO_ONLY = ["ask_kingly", "bookkeeper_seat", "tax_exports", "hauling", "unlimited_texts", "med_alerts", "breeding", "spanish_assistant"];

/** Wraps a Barn/Pro feature: renders children when the plan includes it, else a one-line upgrade card. */
export function Gate({ feature, children, compact = false }) {
  const { ent, has, isLoading } = useEntitlements();
  if (isLoading || !ent || has(feature)) return children;
  const [title, blurb] = COPY[feature] || [FEATURES[feature]?.label || feature, FEATURES[feature]?.blurb || ""];
  const plan = PRO_ONLY.includes(feature) ? "Pro" : "Barn";
  return (
    <div data-testid={`gate-${feature}`}
      className={`rounded-xl border border-amber-300 bg-gradient-to-br from-slate-900 to-slate-800 text-white ${compact ? "p-4" : "p-6 sm:p-8"}`}>
      <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-[0.22em] font-semibold">
        <Lock className="w-3.5 h-3.5" /> Part of the {plan} plan
      </div>
      <h3 className="font-display font-bold text-xl sm:text-2xl mt-2">{title}</h3>
      <p className="text-sm text-slate-300 mt-1 max-w-md">{blurb}</p>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Link to="/settings?tab=plan" onClick={() => trackUpgrade("upgrade_click", feature, "gate")} data-testid={`gate-${feature}-upgrade`}
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-slate-900 font-semibold px-5 py-2.5 text-sm hover:bg-amber-300 transition-colors">
          Move to {plan} <ArrowRight className="w-4 h-4" />
        </Link>
        {PREVIEW[feature] && (
          <Link to={withSrc(PREVIEW[feature][0], "gate")} onClick={() => trackUpgrade("nudge_tap", feature, "gate")} data-testid={`gate-${feature}-preview`}
            className="inline-flex items-center gap-2 rounded-full border border-white/30 text-white font-semibold px-5 py-2.5 text-sm hover:bg-white/10 transition-colors">
            <Eye className="w-4 h-4" /> {PREVIEW[feature][1]}
          </Link>
        )}
      </div>
    </div>
  );
}

/** Tiny "Barn" / "Pro" pill shown next to a menu entry when the current plan doesn't include the feature. */
export function PlanBadge({ feature, className = "" }) {
  const { ent, has } = useEntitlements();
  if (!ent || has(feature)) return null;
  const plan = PRO_ONLY.includes(feature) ? "Pro" : "Barn";
  return (
    <span data-testid={`plan-badge-${feature}`} title={`Part of the ${plan} plan`}
      className={`inline-flex items-center gap-0.5 rounded-full border border-amber-400/70 bg-amber-50 text-amber-800 px-1.5 py-px text-[9px] font-bold uppercase tracking-wider leading-none ${className}`}>
      <Lock className="w-2.5 h-2.5" /> {plan}
    </span>
  );
}
